import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import { Icon } from './Icon'
import { cx } from '@/lib/cx'
import { useScrollLock } from '@/lib/useScrollLock'

/** Long enough to read as a movement, short enough that a front desk never
 *  waits for it. Kept in step with the duration classes below. */
const EXIT_MS = 180

type Phase = 'shut' | 'opening' | 'open' | 'closing'

const SIZES = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-2xl',
  lg: 'sm:max-w-4xl',
} as const

interface Props {
  open: boolean
  onClose: () => void
  title: string
  description?: string | undefined
  size?: keyof typeof SIZES
  /** The body. Lay it out as `flex-1 overflow-y-auto` plus a flush footer — the
   *  panel is a column, so a long form scrolls under a header and footer that
   *  stay put rather than running off the bottom of the screen. */
  children: ReactNode
  /**
   * What takes focus when the dialog opens, instead of the first control.
   *
   * The default is right for a form — the first field is what you came to type
   * in. It is wrong for a confirmation, where the first control is the close
   * button and the safe answer is "cancel".
   */
  initialFocus?: RefObject<HTMLElement | null>
}

/**
 * A centred dialog on a desktop, a bottom sheet on a phone — one component, as
 * the design system asks for.
 *
 * Mounted only while it is on screen, but *not* unmounted the instant `open`
 * goes false: it has to stay long enough to animate out, or a closing dialog
 * just blinks away. `phase` is what buys that time.
 *
 * The motion is the only thing here that is decoration-adjacent, and it earns
 * its place by saying which way the screen went — a form that appears with no
 * transition reads as a page navigation, which is exactly what it is not.
 * `prefers-reduced-motion` flattens it to 0.01ms globally (app.css), and the
 * timeout below still fires, so closing works the same either way.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  children,
  initialFocus,
}: Props) {
  // Always starts shut, even when it is mounted already-open: the panel has to
  // render once in its closed state for the transition to have somewhere to
  // move from. Initialising to 'open' renders it settled and nothing animates.
  const [phase, setPhase] = useState<Phase>('shut')
  const panel = useRef<HTMLDivElement>(null)
  const returnTo = useRef<HTMLElement | null>(null)
  const headingId = useId()
  const descriptionId = useId()

  // Open: mount shut, then flip a frame later so the transition has two states
  // to move between. Close: mark closing, unmount when the movement is done.
  useEffect(() => {
    if (open) {
      setPhase((p) => (p === 'open' ? p : 'opening'))
      // Two frames, not one. A single frame can still hand the browser both the
      // closed and the open style before it paints either, and a transition
      // between two styles it never painted does not run at all.
      let inner = 0
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setPhase('open'))
      })
      return () => {
        cancelAnimationFrame(outer)
        cancelAnimationFrame(inner)
      }
    }
    setPhase((p) => (p === 'shut' ? p : 'closing'))
    const timer = setTimeout(() => setPhase('shut'), EXIT_MS)
    return () => clearTimeout(timer)
  }, [open])

  // Whatever opened the dialog gets the focus back when it closes — otherwise
  // focus falls to the top of the document and the keyboard user starts over.
  //
  // Read during render, not in an effect: React applies `autoFocus` while it
  // commits, which is *before* effects run, so an effect here would record the
  // dialog's own first field as the place focus came from and hand it back to
  // itself.
  if (open && returnTo.current === null) {
    returnTo.current = document.activeElement as HTMLElement | null
  }

  useEffect(() => {
    if (!open) return
    return () => {
      const opener = returnTo.current
      returnTo.current = null
      opener?.focus?.()
    }
  }, [open])

  const focusables = useCallback(
    () =>
      Array.from(
        panel.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((el) => el.offsetParent !== null || el === document.activeElement),
    [],
  )

  // The first field, not the close button: the point of opening this is to
  // type. A caller that knows better says so with `initialFocus`.
  useEffect(() => {
    if (phase !== 'open') return
    const target = initialFocus?.current ?? focusables()[0]
    if (target && !panel.current?.contains(document.activeElement)) target.focus()
  }, [phase, focusables, initialFocus])

  // Nothing behind the dialog scrolls while it is up. A phone especially will
  // happily scroll the page under a sheet and lose the caller's place. Shared
  // with the navigation drawer, and counted, so a sheet opened from a screen
  // reached through the drawer does not unlock the page when it closes.
  useScrollLock(phase !== 'shut')

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      // A select or a calendar inside the form answers Escape first.
      if ((event.target as Element).closest?.('[aria-expanded="true"]')) return
      event.preventDefault()
      onClose()
      return
    }
    if (event.key !== 'Tab') return
    // A dialog that lets Tab wander behind it is not modal in any sense that
    // matters to someone who cannot see where the focus went.
    const items = focusables()
    if (items.length === 0) return
    const first = items[0]!
    const last = items[items.length - 1]!
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  if (phase === 'shut') return null
  const shown = phase === 'open'

  return createPortal(
    <div className="fixed inset-0 z-sheet flex items-end justify-center sm:items-center sm:p-6">
      {/* A real button, so the only way to dismiss by pointer is also reachable
          by a screen reader that is walking the dialog's siblings. */}
      <button
        type="button"
        tabIndex={-1}
        aria-label="Close"
        onClick={onClose}
        className={cx(
          'absolute inset-0 z-scrim cursor-default border-0 bg-overlay transition-opacity duration-200 ease-standard',
          shown ? 'opacity-100' : 'opacity-0',
        )}
      />

      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        aria-describedby={description ? descriptionId : undefined}
        onKeyDown={onKeyDown}
        className={cx(
          'relative z-sheet flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-xl border border-line-soft bg-surface shadow-lg',
          'transition-[opacity,transform] duration-200 ease-standard',
          'sm:max-h-[85dvh] sm:rounded-xl',
          SIZES[size],
          // Up from the bottom edge on a phone, forward out of the page on a
          // desktop: each matches the direction the surface came from.
          shown ? 'translate-y-0 opacity-100 sm:scale-100' : 'translate-y-4 opacity-0 sm:scale-[0.98]',
        )}
      >
        <div className="flex flex-none items-start gap-4 border-b border-line-soft px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2 id={headingId} className="text-lg leading-tight font-semibold text-fg">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="mt-1 text-sm leading-snug text-fg-muted">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid size-touch flex-none place-items-center rounded-md text-fg-subtle transition-colors duration-150 ease-standard hover:bg-hover hover:text-fg"
          >
            <Icon name="close" size="1.125rem" />
          </button>
        </div>

        {children}
      </div>
    </div>,
    document.body,
  )
}

/** The scrolling part of a modal form. */
export function ModalBody({ children }: { children: ReactNode }) {
  return <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">{children}</div>
}

/** Pinned under the body, so the save is reachable without scrolling to it. */
export function ModalFooter({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-none items-center justify-end gap-3 border-t border-line-soft bg-subtle px-5 py-3 pb-safe-3 sm:pb-3">
      {children}
    </div>
  )
}
